import React, { createContext, useContext, useState, useEffect } from 'react'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Load saved password immediately or use default
  const [adminPassword, setAdminPassword] = useState(() => {
    const savedPassword = localStorage.getItem('nursingAdminPassword')
    return savedPassword || 'admin123'
  })

  // Default admin credentials
  const ADMIN_CREDENTIALS = {
    email: 'admin@nursingexam.com',
    get password() {
      return adminPassword
    }
  }

  useEffect(() => {
    // Check if user is already logged in
    const savedAuth = localStorage.getItem('nursingAdminAuth')
    
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth)
        if (authData.isAuthenticated && authData.user) {
          setIsAuthenticated(true)
          setUser(authData.user)
        }
      } catch (error) {
        console.error('Error parsing saved auth data:', error)
        localStorage.removeItem('nursingAdminAuth')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email, password) => {
    try {
      // Simple credential check (in production, use proper authentication)
      if (email === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
        const userData = {
          email: email,
          name: 'Admin User',
          role: 'admin',
          loginTime: new Date().toISOString()
        }
        
        setIsAuthenticated(true)
        setUser(userData)
        
        // Save to localStorage
        localStorage.setItem('nursingAdminAuth', JSON.stringify({
          isAuthenticated: true,
          user: userData
        }))
        
        return { success: true }
      } else {
        return { success: false, error: 'Invalid email or password' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: 'An error occurred during login' }
    }
  }

  const logout = () => {
    setIsAuthenticated(false)
    setUser(null)
    localStorage.removeItem('nursingAdminAuth')
  }

  const changePassword = async (currentPassword, newPassword) => {
    try {
      // Verify current password
      if (currentPassword !== ADMIN_CREDENTIALS.password) {
        return { success: false, error: 'Current password is incorrect' }
      }

      if (newPassword.length < 6) {
        return { success: false, error: 'New password must be at least 6 characters' }
      }

      // Update the password
      setAdminPassword(newPassword)
      localStorage.setItem('nursingAdminPassword', newPassword)
      
      console.log('Password changed successfully for:', user.email)
      
      return { 
        success: true, 
        message: 'Password changed successfully! You can now login with your new password.' 
      }
    } catch (error) {
      console.error('Change password error:', error)
      return { success: false, error: 'An error occurred while changing password' }
    }
  }

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    changePassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
